import { useContext, useEffect, useMemo, useState } from 'react';
import { ScoringContext } from '@/contexts/scoring/context';
import {
  filterAdvancedTableData,
  generateAdvancedStatsAllSportsHeader,
  generateAdvancedStatsBasketballHeader,
} from '@/components/AdvancedStatsTable/utils';
import {
  AdvancedStatsData,
  AdvancedStatsTableHeader,
} from '@/components/AdvancedStatsTable/types';
import { StatsContext } from '@/contexts/stats/context';
import { InfoMessage } from '@/components/common/InfoMessage';
import { SPORT_ID } from '@/service/constants';
import { COMMON_STRING } from '@/constants/dictionary';
import { getPlayerShirtNumber } from '../FixtureTabs/helpers/getPlayerNameAndNumber';
import { AdvancedStatsBasketballTable } from './Tables/AdvancedStatsBasketballTable';
import { AdvancedStatsAllSportsTable } from './Tables/AdvancedStatsAllSportsTable';

export const AdvancedStatsTable = () => {
  const {
    state: { fixtureSummary, currentSport },
    fixtureConfigState: { fixtureConfig },
  } = useContext(ScoringContext);
  const {
    activeButtons: { quarter, type, team },
  } = useContext(StatsContext);
  const [newTableHeader, setNewTableHeader] =
    useState<AdvancedStatsTableHeader>();

  const advancedStatsData: AdvancedStatsData[] = useMemo(() => {
    const data =
      filterAdvancedTableData({
        activeQuarter: quarter,
        activeType: type,
        activeTeam: team,
        actions: fixtureSummary?.teamActionAggregates,
        homeTeamId: fixtureSummary?.homeTeam.id,
      }) ?? [];

    if (data.length && 'teamId' in data[0]) {
      return data;
    }

    return [...data].sort((a, b) => {
      const numA =
        parseInt(
          getPlayerShirtNumber(
            (a as any).playerId,
            fixtureConfig,
            fixtureSummary,
          ),
          10,
        ) || Infinity;
      const numB =
        parseInt(
          getPlayerShirtNumber(
            (b as any).playerId,
            fixtureConfig,
            fixtureSummary,
          ),
          10,
        ) || Infinity;
      return numA - numB;
    });
  }, [quarter, type, team, fixtureSummary, fixtureConfig]);

  const isBasketball = currentSport && currentSport.id === SPORT_ID.BASKETBALL;

  useEffect(() => {
    if (advancedStatsData) {
      isBasketball
        ? setNewTableHeader(
            generateAdvancedStatsBasketballHeader(type, fixtureConfig),
          )
        : setNewTableHeader(
            generateAdvancedStatsAllSportsHeader(advancedStatsData),
          );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedStatsData, isBasketball, type]);

  if (!advancedStatsData || !advancedStatsData.length) {
    return (
      <InfoMessage message={COMMON_STRING.ADVANCED_STATS_DOES_NOT_EXIST} />
    );
  }
  if (isBasketball) {
    return (
      <AdvancedStatsBasketballTable
        data={advancedStatsData}
        headerData={newTableHeader}
      />
    );
  }
  return (
    <AdvancedStatsAllSportsTable
      data={advancedStatsData}
      headerData={newTableHeader}
    />
  );
};
